/*
 *  Copyright (c) Qwoach LLC 2026. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 */

import {plainToClass} from "@qwoach/class-transformer-wrapper";
import GenogramCustomConnectionItem from "@shared/Genogram/GenogramCustomConnectionItem";
import {LineTypeItem} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/LineTypeItem";
import GenogramCustomConnectionRenderManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramCustomConnectionRenderManager";

const CUSTOM_LINE_SUB_TYPE_PREFIX = "custom:"

export default class GenogramCustomConnectionsSyncManager {

	customConnectionsById = new Map()
	customConnectionsList = []
	isLoaded = false

	constructor({dataManager = null} = {}) {
		this.dataManager = dataManager
		this.renderManager = new GenogramCustomConnectionRenderManager()
	}

	static buildLineSubType(id = "") {
		return `${CUSTOM_LINE_SUB_TYPE_PREFIX}${id}`
	}

	static isCustomLineSubType(lineSubType = "") {
		return typeof lineSubType === "string" && lineSubType.startsWith(CUSTOM_LINE_SUB_TYPE_PREFIX)
	}

	static extractCustomConnectionId(lineSubType = "") {
		if(!GenogramCustomConnectionsSyncManager.isCustomLineSubType(lineSubType)){
			return ""
		}
		return lineSubType.substring(CUSTOM_LINE_SUB_TYPE_PREFIX.length)
	}

	async loadDefinitions(forceReload = false) {
		if(this.isLoaded && !forceReload){
			return this.customConnectionsList
		}

		let items = await this.dataManager?.getGenogramCustomConnections?.()
		this.replaceCache(items || [])
		this.isLoaded = true
		return this.customConnectionsList
	}

	getAll() {
		return this.customConnectionsList.slice()
	}

	getById(id) {
		return this.customConnectionsById.get(id) || null
	}

	getByLineSubType(lineSubType = "") {
		let itemId = GenogramCustomConnectionsSyncManager.extractCustomConnectionId(lineSubType)
		if(!itemId){
			return null
		}
		return this.getById(itemId)
	}

	createLineTypeItem(customConnectionItem) {
		if(!customConnectionItem){
			return null
		}
		let item = new LineTypeItem(
			customConnectionItem.title,
			GenogramCustomConnectionsSyncManager.buildLineSubType(customConnectionItem.id),
			null,
			customConnectionItem.connectionCategory
		)
		item.isCustomConnection = true
		item.customConnectionId = customConnectionItem.id
		item.previewSrc = this.buildPreviewDataUrl(customConnectionItem)
		return item
	}

	getCustomLineTypeItems() {
		return this.customConnectionsList.map(item => this.createLineTypeItem(item)).filter(Boolean)
	}

	buildPreviewDataUrl(customConnectionItem, options = {}) {
		let item = customConnectionItem?.renderConfig ? customConnectionItem : this.getById(customConnectionItem?.id || customConnectionItem)
		if(!item){
			return ""
		}
		return this.renderManager.buildPreviewDataUrl(item.renderConfig || {}, {
			...options,
			connectionCategory: item.connectionCategory,
		})
	}

	getRenderManager() {
		return this.renderManager
	}

	getRenderConfigByLineSubType(lineSubType = "") {
		return this.getByLineSubType(lineSubType)?.renderConfig || null
	}

	getConnectionCategoryByLineSubType(lineSubType = "") {
		return this.getByLineSubType(lineSubType)?.connectionCategory || null
	}

	async upsert(customConnectionItem) {
		let nextItem = plainToClass(GenogramCustomConnectionItem, JSON.parse(JSON.stringify(customConnectionItem || {})))
		nextItem.normalize()
		await this.dataManager?.setGenogramCustomConnectionItem?.(nextItem)
		this.upsertInCache(nextItem)
		return nextItem
	}

	async deleteById(id) {
		if(!id){
			return
		}
		await this.dataManager?.deleteGenogramCustomConnectionItem?.({id})
		this.customConnectionsById.delete(id)
		this.customConnectionsList = this.customConnectionsList.filter(item => item.id !== id)
	}

	syncGenogramItem(genogramItem) {
		let edges = genogramItem?.jsonData?.elements?.edges
		if(!Array.isArray(edges)){
			return {isChanged: false, removedIds: []}
		}

		let isChanged = false
		let removedIds = []
		let nextEdges = []
		for(let edge of edges){
			let lineSubType = edge?.data?.lineSubType || ""
			if(!GenogramCustomConnectionsSyncManager.isCustomLineSubType(lineSubType)){
				nextEdges.push(edge)
				continue
			}

			let customConnectionItem = this.getByLineSubType(lineSubType)
			if(!customConnectionItem){
				removedIds.push(edge?.data?.id)
				isChanged = true
				continue
			}

			let nextTitle = customConnectionItem.title || edge?.data?.title || ""
			if(edge.data.title !== nextTitle){
				edge.data.title = nextTitle
				isChanged = true
			}
			if(edge.data.connectionCategory !== customConnectionItem.connectionCategory){
				edge.data.connectionCategory = customConnectionItem.connectionCategory
				isChanged = true
			}
			if(edge.data.customConnectionRenderConfig != null){
				delete edge.data.customConnectionRenderConfig
				isChanged = true
			}
			nextEdges.push(edge)
		}
		if(isChanged){
			genogramItem.jsonData.elements.edges = nextEdges
		}
		return {isChanged, removedIds}
	}

	syncCyEdges(cy) {
		if(!cy){
			return {isChanged: false}
		}
		let isChanged = false
		let removedEdges = []
		cy.edges().forEach(edge => {
			let lineSubType = edge.data("lineSubType") || ""
			if(!GenogramCustomConnectionsSyncManager.isCustomLineSubType(lineSubType)){
				return
			}
			let customConnectionItem = this.getByLineSubType(lineSubType)
			if(!customConnectionItem){
				removedEdges.push(edge)
				isChanged = true
				return
			}
			let nextTitle = customConnectionItem.title || edge.data("title") || ""
			if(edge.data("title") !== nextTitle){
				edge.data({title: nextTitle})
				isChanged = true
			}
			if(edge.data("connectionCategory") !== customConnectionItem.connectionCategory){
				edge.data({connectionCategory: customConnectionItem.connectionCategory})
				isChanged = true
			}
			if(edge.data("customConnectionRenderConfig") != null){
				edge.removeData("customConnectionRenderConfig")
				isChanged = true
			}
		})
		if(removedEdges.length){
			removedEdges.forEach(edge => edge.remove())
		}
		if(isChanged){
			cy.trigger("cyeditor.linetype")
			cy.trigger("cyeditor.data")
		}
		return {isChanged}
	}

	replaceCache(items = []) {
		let nextItems = items.map(item => {
			let nextItem = plainToClass(GenogramCustomConnectionItem, JSON.parse(JSON.stringify(item || {})))
			nextItem.normalize()
			return nextItem
		})
		this.customConnectionsById = new Map()
		nextItems.forEach(item => this.customConnectionsById.set(item.id, item))
		this.customConnectionsList = nextItems
	}

	upsertInCache(customConnectionItem) {
		let existingIndex = this.customConnectionsList.findIndex(item => item.id === customConnectionItem.id)
		if(existingIndex >= 0){
			this.customConnectionsList.splice(existingIndex, 1, customConnectionItem)
		} else {
			this.customConnectionsList.push(customConnectionItem)
		}
		this.customConnectionsById.set(customConnectionItem.id, customConnectionItem)
	}
}

export {
	CUSTOM_LINE_SUB_TYPE_PREFIX,
}
